import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatPrice, formatDate, statusLabel } from "@/lib/format";
import { AlertTriangle, Package, ClipboardList, Wallet, RefreshCw } from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  const load = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (!stats) return <div className="h-40 rounded-3xl bg-white animate-pulse" />;

  const cards = [
    { label: "Commandes en attente", value: stats.pending_orders, icon: ClipboardList, color: "bg-rose/60", testid: "admin-stat-pending" },
    { label: "Chiffre d'affaires livré", value: formatPrice(stats.revenue_livree), icon: Wallet, color: "bg-mint/40", testid: "admin-stat-revenue" },
    { label: "Produits au catalogue", value: stats.products_total, icon: Package, color: "bg-sky2/40", testid: "admin-stat-products" },
    { label: "Commandes reçues", value: stats.orders_total, icon: ClipboardList, color: "bg-tan/50", testid: "admin-stat-orders" },
  ];

  return (
    <div data-testid="admin-dashboard-content">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-teal-dark">Tableau de bord</h1>
          <p className="text-sm text-ink/60 mt-1">Bonjour ! Voici l'état de votre boutique aujourd'hui.</p>
        </div>
        <button data-testid="admin-stats-refresh" onClick={load} className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm text-ink hover:border-teal hover:text-teal transition-colors">
          <RefreshCw size={15} /> Actualiser
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} data-testid={c.testid} className={`rounded-3xl ${c.color} border border-white p-6`}>
              <Icon size={20} className="text-teal" />
              <p className="mt-3 text-2xl font-semibold text-teal-dark">{c.value}</p>
              <p className="text-sm text-ink/70">{c.label}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl border border-line p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-[#b47b2e]" />
            <h2 className="font-serif text-xl text-teal-dark">Stock bas (≤ 5)</h2>
          </div>
          {stats.low_stock.length === 0 ? (
            <p className="text-sm text-ink/60">Tous vos stocks sont confortables, félicitations !</p>
          ) : (
            <ul className="space-y-2">
              {stats.low_stock.map((p) => (
                <li key={p.id} data-testid={`admin-low-stock-${p.id}`} className="flex items-center justify-between text-sm py-2 border-b border-line/60 last:border-0">
                  <span className="text-ink truncate pr-4">{p.name}</span>
                  <span className={`font-semibold whitespace-nowrap ${p.stock <= 2 ? "text-[#b04a4a]" : "text-[#b47b2e]"}`}>
                    {p.stock} en stock
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link data-testid="admin-low-stock-link" to="/admin/produits" className="mt-4 inline-block text-sm text-teal underline">Gérer les stocks</Link>
        </div>

        <div className="bg-white rounded-3xl border border-line p-6">
          <h2 className="font-serif text-xl text-teal-dark mb-4">Dernières commandes</h2>
          {stats.recent_orders.length === 0 ? (
            <p className="text-sm text-ink/60">Aucune commande pour le moment.</p>
          ) : (
            <ul className="space-y-3">
              {stats.recent_orders.map((o) => (
                <li key={o.id} data-testid={`admin-recent-order-${o.ref}`} className="flex items-center justify-between text-sm py-2 border-b border-line/60 last:border-0">
                  <div>
                    <p className="font-mono text-teal font-medium">{o.ref}</p>
                    <p className="text-xs text-ink/60">{o.customer_name} — {formatDate(o.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-teal">{formatPrice(o.total)}</p>
                    <p className="text-xs text-mauve">{statusLabel(o.status)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Link data-testid="admin-recent-orders-link" to="/admin/commandes" className="mt-4 inline-block text-sm text-teal underline">Voir toutes les commandes</Link>
        </div>
      </div>
    </div>
  );
}
